import React, { useEffect, useState } from "react";
import {
    Grid,
    Typography,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Box,
    MenuItem,
    TableContainer,
    Tooltip,
    Avatar
} from "@mui/material";
import Paper from "@mui/material/Paper";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDocumentTitle } from "../../Hooks/useDocumentTitle";
import { useApplicationContext } from "../../ApplicationContext";
import { GetHostSystemData, AddFileTypeData, GetFileTypeData, FileTypeClass } from "../../apiClient/Api";
import IconPlaceholder from "./IconPlaceholder";
import { DataGrid, GridActionsCellItem, GridColDef, GridRowParams, GridToolbarQuickFilter, Toolbar } from "@mui/x-data-grid";
import { useFileTypeClassTranslations } from "../../Hooks/FileTypeClass";

/**
 * Displays and manages the admin overview for available file types.
 */
export const AdminFileTypes = () => {
    useDocumentTitle("base.pages.fileTypes");
    const { t } = useTranslation();
    const fileTypeTranslations = useFileTypeClassTranslations();

    const [rows, setRows] = useState<GetFileTypeData[]>([]);
    const [hostSystems, setHostSystems] = useState<GetHostSystemData[]>([]);
    const [isData, setIsData] = useState(false);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [newAppName, setNewAppName] = useState("");
    const [newAppHostSystem, setNewAppHostSystem] = useState("");
    const [deleteDialogTarget, setDeleteDialogTarget] = useState<GetFileTypeData | null>(null);
    const [deleteEntryValue, setDeleteEntryValue] = useState("");
    const context = useApplicationContext();
    const controller = context.api.fileType;
    const history = useNavigate();

    /**
     * Opens the dialog for adding a new application entry.
     */
    const handleOpenAddDialog = () => setIsAddDialogOpen(true);

    /**
     * Closes the dialog for adding a new application entry.
     */
    const handleCloseAddDialog = () => {
        setIsAddDialogOpen(false);
        setNewAppName("");
        setNewAppHostSystem("");
    };

    /**
     * Sends a request to create a new application/file type entry.
     */
    const handleAddApp = async () => {
        const newApp: AddFileTypeData = {
            name: newAppName,
            description: "",
            filterTags: [],
            variants: [],
            packageId: "",
            platformConfigurations: newAppHostSystem
                ? [
                    {
                        hostSystemId: newAppHostSystem,
                        matchingFiles: [],
                    },
                ]
                : [],
        };
        try {
            const response = await controller.fileTypeAddFileType(newApp);
            if (response.data) {
                setRows(current => [...current, response.data]);
                handleCloseAddDialog();
            }
        } catch (e) {
            context.apiError(e);
        }
    };

    /**
     * Opens the delete dialog for the provided file type row.
     * @param fileType File type that should be deleted.
     */
    const handleDeleteClick = (fileType: GetFileTypeData) => {
        setDeleteDialogTarget(fileType);
        setDeleteEntryValue("");
    };

    /**
     * Closes the delete dialog and clears the form state.
     */
    const handleDeleteDialogClose = () => {
        setDeleteDialogTarget(null);
        setDeleteEntryValue("");
    };

    /**
     * Confirms the deletion of the selected file type.
     */
    const handleConfirmDelete = async () => {
        if (!deleteDialogTarget || deleteEntryValue !== deleteDialogTarget.name) {
            return;
        }
        try {
            await controller.fileTypeDeleteFileType(deleteDialogTarget.id);
            setRows(current => current.filter(r => r.id !== deleteDialogTarget.id));
            handleDeleteDialogClose();
        } catch (e) {
            context.apiError(e);
        }
    };

    /**
     * Loads host systems and file type data from the API.
     */
    const fetchData = async () => {
        try {
            const hostSystemsResponse = await controller.fileTypeGetHostSystems();
            setHostSystems(hostSystemsResponse.data);
            const fileTypesResponse = await controller.fileTypeGetFileTypes();
            setRows(fileTypesResponse.data);
        } catch (e) {
            context.apiError(e);
        }
    };

    useEffect(() => {
        if (!isData) {
            setIsData(true);
            fetchData();
        }
    }, [isData]);

    /**
     * Creates the public logo url for the provided image id.
     * @param logoId Identifier of the logo image.
     */
    const getLogoUrl = (logoId: string | null) => {
        // @ts-ignore
        const serverUrl = ServerConfig.SERVER_URL;
        return logoId ? `${serverUrl}/FileType/Image/${logoId}` : "";
    };

    const isDeleteDisabled = !deleteDialogTarget || deleteEntryValue !== deleteDialogTarget.name;

    const EditToolbar = () => (
        <Toolbar style={{ display: "block", minHeight: "auto", padding: 0, borderBottom: "none" }}>
            <Grid container sx={{ marginTop: 2, marginBottom: 1 }}>
                <Grid size={{ md: 8 }}>
                    <Button color="primary" startIcon={<AddIcon />} onClick={handleOpenAddDialog}>
                        {t("adminFileTypes.addButton")}
                    </Button>
                </Grid>
                <Grid size={{ md: 4 }}>
                    <GridToolbarQuickFilter />
                </Grid>
            </Grid>
        </Toolbar>
    );

    const columns: GridColDef[] = [
        {
            field: "icon",
            headerName: t("adminFileTypes.table.icon"),
            width: 80,
            renderCell: (params) => (
                <Avatar
                    src={params.row.iconId ? getLogoUrl(params.row.iconId) : undefined}
                    sx={{ width: 40, height: 40 }}
                >
                    {!params.row.iconId && <IconPlaceholder />}
                </Avatar>
            ),
            sortable: false,
            filterable: false,
        },
        {
            field: "name",
            headerName: t("adminFileTypes.table.name"),
            flex: 1,
            minWidth: 200,
        },
        {
            field: "variant",
            headerName: t("adminFileTypes.table.variant"),
            flex: 1,
            minWidth: 180,
            valueGetter: (value, row) => fileTypeTranslations[row.variant ?? FileTypeClass.UNDEFINED] || "-",
        },
        {
            field: "packageId",
            headerName: t("adminFileTypes.table.packageId"),
            flex: 1,
            minWidth: 200,
            valueGetter: (value) => value || "-",
        },
        {
            field: "public",
            headerName: t("adminFileTypes.table.public"),
            width: 100,
            type: "boolean",
        },
        {
            field: "actions",
            type: "actions",
            headerName: t("adminFileTypes.table.actions"),
            width: 120,
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem
                    icon={<Tooltip title={t("adminFileTypes.table.edit")}><EditIcon /></Tooltip>}
                    label={t("adminFileTypes.table.edit")}
                    onClick={() => history(`/admin/filetypes/${params.row.id}`)}
                    showInMenu={false}
                />,
                <GridActionsCellItem
                    icon={<Tooltip title={t("adminFileTypes.table.delete")}><DeleteIcon /></Tooltip>}
                    label={t("adminFileTypes.table.delete")}
                    onClick={() => handleDeleteClick(params.row)}
                    showInMenu={false}
                />,
            ],
        },
    ];

    return (
        <>
            <Dialog
                open={deleteDialogTarget !== null}
                onClose={handleDeleteDialogClose}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">{t("adminFileTypes.deleteDialog.title")}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        {t("adminFileTypes.deleteDialog.description")}
                    </DialogContentText>
                    {deleteDialogTarget && (
                        <>
                            <DialogContentText sx={{ fontWeight: 'bold' }}>
                                {t("adminFileTypes.deleteDialog.instruction", { name: deleteDialogTarget.name })}
                            </DialogContentText>
                            <TextField
                                autoFocus
                                margin="dense"
                                label={t("adminFileTypes.deleteDialog.textFieldLabel")}
                                value={deleteEntryValue}
                                onChange={e => setDeleteEntryValue(e.target.value)}
                                fullWidth
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose} color="primary">
                        {t("adminFileTypes.deleteDialog.cancel")}
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" disabled={isDeleteDisabled}>
                        {t("adminFileTypes.deleteDialog.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isAddDialogOpen} onClose={handleCloseAddDialog}>
                <DialogTitle>{t("adminFileTypes.addDialog.title")}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t("adminFileTypes.addDialog.nameLabel")}
                        fullWidth
                        value={newAppName}
                        onChange={e => setNewAppName(e.target.value)}
                    />
                    <TextField
                        select
                        margin="dense"
                        label={t("adminFileTypes.addDialog.hostSystemLabel")}
                        fullWidth
                        value={newAppHostSystem}
                        onChange={e => setNewAppHostSystem(e.target.value)}
                    >
                        <MenuItem value="">{t("adminFileTypes.addDialog.hostSystemPlaceholder")}</MenuItem>
                        {hostSystems.map((hostSystem, index) => (
                            <MenuItem key={hostSystem.id ?? hostSystem.name ?? `host-${index}`} value={hostSystem.id ?? ""}>
                                {hostSystem.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddDialog} color="primary">
                        {t("adminFileTypes.addDialog.cancel")}
                    </Button>
                    <Button onClick={handleAddApp} color="primary" disabled={!newAppName.trim()}>
                        {t("adminFileTypes.addDialog.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <TableContainer component={Paper} style={{ margin: 4 }}>
                <DataGrid
                    showToolbar
                    slots={{
                        toolbar: EditToolbar,
                    }}
                    rows={rows}
                    columns={columns}
                    autoHeight
                    initialState={{
                        sorting: {
                            sortModel: [{ field: "name", sort: "asc" }],
                        },
                    }}
                />
            </TableContainer>
        </>
    );
};
